import { DropdownItem } from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { useSSFBehaviors } from 'hooks';
import { useUserTypeSwitch } from 'hooks/useUserTypeSwitch';
import { nonPaginatedApolloClient } from 'providers/ApolloClients/NonPaginatedApolloClient';
import { QUERY_fetchAgencyCommunities, QUERY_fetchFilteredCommunities } from 'queries';
import { FC, useContext, useMemo } from 'react';
import { useUserAgencyIdSelector } from 'store/selectors/userSelectors/userSelector';
import { GQLAPIContextType } from 'types/common.types';
import {
  FetchAgencyCommunitiesQuery,
  FetchFilteredCommunitiesQuery,
  FetchFilteredCommunitiesQueryVariables,
} from 'types/graphql';
import { createGQLDataContext } from 'utils/createDataContext';

export const TeamMemberEditRankingDataContext = createGQLDataContext({} as GQLAPIContextType);

const client = nonPaginatedApolloClient();

export const TeamMemberEditRankingDataProvider: FC = ({ children }) => {
  const agencyId = useUserAgencyIdSelector();

  const [values, useQueryOptions] = useSSFBehaviors<
    { data: FetchFilteredCommunitiesQuery | FetchAgencyCommunitiesQuery },
    DropdownItem[]
  >(
    useUserTypeSwitch({
      Agency: {
        key: 'TEAM_MEMBER_AGENCY_EDIT_RANKING_LOCATIONS',
        query: QUERY_fetchAgencyCommunities,
        client,
        variables: {
          id: agencyId,
        },
        pageSize: 20,
        debounceTimeout: 500,
        paginationType: 'InfiniteScroll',
        responseTransformer: (data?: FetchAgencyCommunitiesQuery) => {
          if (!data) return [];

          const mapped =
            data?.fetchAgency?.agencyCommunities?.map((community) => {
              return {
                id: community.id,
                value: `${community.id}`,
                label: community.name,
                key: community.id,
              };
            }) || [];
          return {
            options: mapped,
            hasNextPage: false,
          };
        },
      },
      B2B: {
        key: 'TEAM_MEMBER_AGENCY_EDIT_RANKING_LOCATIONS',
        query: QUERY_fetchFilteredCommunities,
        client,
        pageSize: 20,
        debounceTimeout: 500,
        paginationType: 'InfiniteScroll',
        payloadTransformer: (values: FetchFilteredCommunitiesQueryVariables) => ({
          ...values,
          filter: {
            withEnabledFeatures: ['MULTI_LOCATION_TIERING'],
          },
        }),
        responseTransformer: (data?: FetchFilteredCommunitiesQuery) => {
          if (!data) return [];
          const mapped =
            data?.fetchCommunities?.nodes?.map((node) => {
              return {
                id: node?.id,
                value: `${node?.id}`,
                label: node?.name,
                key: node?.id,
              };
            }) || [];

          return { options: mapped, hasNextPage: data?.fetchCommunities?.pageInfo?.hasNextPage };
        },
      },
    }),
  );

  const value = useMemo(() => {
    return { values, ...useQueryOptions };
  }, [values, useQueryOptions]);

  return (
    <TeamMemberEditRankingDataContext.Provider value={value}>
      {children}
    </TeamMemberEditRankingDataContext.Provider>
  );
};

export const useTeamMemberEditRankingDataContext = (): GQLAPIContextType<{
  options: DropdownItem[];
  hasNextPage: boolean;
}> => useContext(TeamMemberEditRankingDataContext);
